/**
 * BrandPaletteWizard — deck screen 01 (`concepts/brand-palette-wizard/01`).
 *
 * "A wizard for users to create a Brand Color Palette where they select the hex
 * colors themselves and save it to be reused." (concept index.md). The user
 * names the palette, edits a list of exact-hex stops (circular swatch · # hex
 * input · remove), sees it live on a canonical brand-Wheel preview + an
 * overlapping-circle chip row, and Saves — which persists through the SAME
 * existing savePalette API the rest of the palette system uses, so the palette
 * then appears in the Color Library for reuse everywhere (deck screen 02).
 *
 * Chrome reuses the real ColorPalette modal styles (ModalContainer / header /
 * LibraryButton) so it matches the app's existing palette surfaces. Gated by
 * REACT_APP_BRAND_PALETTE_WIZARD (see featureFlag.js) — the caller only mounts
 * this when the flag is ON.
 *
 * Props:
 *   onClose()    — dismiss the wizard
 *   onLibrary()  — open the Color Library (top-right Library button)
 *   onSaved()    — called after a successful save (caller refetches the library)
 *   wheelId      — current wheel id (passed through to savePalette)
 *   usageType    — 1 wheel / 2 segment (which library list the palette joins)
 */
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { IconButton, Modal } from "@mui/material";
import React, { useState } from "react";
import { toast } from "react-toastify";
import ColorLibraryIcon from "../../../../../assets/icons/ColorLibrary";
import { savePalette } from "../../../../../services/api";
import Button from "../../../../Button";
import TextView from "../../../../TextView";
import CustomColorPicker from "../../../../CustomColorPicker";
import * as Chrome from "../styles";
import BrandWheelPreview from "./BrandWheelPreview";
import {
  addStop,
  buildSavePayload,
  initialStops,
  isSaveable,
  MAX_STOPS,
  MIN_STOPS,
  paletteHexes,
  removeStop,
  setStopDraft,
  setStopHex,
  snapDraft,
} from "./logic";
import * as S from "./styles";

export default function BrandPaletteWizard({
  onClose = () => {},
  onLibrary = () => {},
  onSaved = () => {},
  wheelId,
  usageType = 2,
}) {
  const [name, setName] = useState("Brand colors");
  const [stops, setStops] = useState(() => initialStops());
  const [activeStopId, setActiveStopId] = useState(null);
  const [saving, setSaving] = useState(false);

  const hexes = paletteHexes(stops);
  const canSave = isSaveable({ name, stops }) && !saving;
  const activeStop = stops.find((s) => s.id === activeStopId) || null;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = buildSavePayload({ name, stops, wheelId, usageType });
      const response = await savePalette(payload);
      if (response?.data?.success) {
        toast.success("Palette saved successfully!");
        onSaved();
        onClose();
      } else {
        toast.error(response?.data?.message || "Failed to save palette. Please try again.");
      }
    } catch (error) {
      console.error("Error saving brand palette:", error);
      toast.error("An error occurred while saving the palette.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose}>
      <Chrome.ModalContainer
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "88vh",
        }}
      >
        <Chrome.ModalHeader>
          <div>
            <TextView variant="title">Brand Palette</TextView>
            <TextView>
              Pick your exact brand colors and save the palette to reuse everywhere.
            </TextView>
          </div>
          <Chrome.Row>
            <Chrome.LibraryButton onClick={onLibrary} data-bpw-library>
              <ColorLibraryIcon />
              <TextView>Library</TextView>
            </Chrome.LibraryButton>
            <IconButton onClick={onClose} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Chrome.Row>
        </Chrome.ModalHeader>

        <S.Body>
          <S.Grid>
            {/* left: live preview */}
            <S.PreviewCol>
              <S.PreviewHeading>Preview</S.PreviewHeading>
              <S.PreviewSub>Your Brand Palette, previewed on a Wheel.</S.PreviewSub>
              <S.PreviewCard>
                <BrandWheelPreview colors={hexes} />
                <S.PreviewName>{name.trim() || "Untitled palette"}</S.PreviewName>
                <S.ChipRow aria-hidden="true">
                  {hexes.map((hex, i) => (
                    <S.Chip key={i} style={{ backgroundColor: hex }} />
                  ))}
                </S.ChipRow>
              </S.PreviewCard>
            </S.PreviewCol>

            {/* right: form */}
            <S.FormCol>
              <S.Field>
                <S.Label htmlFor="bpw-name">Palette name</S.Label>
                <S.NameInput
                  id="bpw-name"
                  value={name}
                  maxLength={60}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Brand colors"
                  data-bpw-name
                />
              </S.Field>

              <S.Field>
                <S.ColorsHead>
                  <S.Label as="div">Colors</S.Label>
                  <S.CountPill data-bpw-count>
                    {stops.length} color{stops.length === 1 ? "" : "s"}
                  </S.CountPill>
                </S.ColorsHead>

                <S.StopList>
                  {stops.map((stop) => {
                    const active = stop.id === activeStopId;
                    return (
                      <S.StopRow key={stop.id} data-bpw-stop>
                        <S.Swatch
                          type="button"
                          color={stop.hex}
                          active={active}
                          aria-label={`Edit color ${stop.hex}`}
                          onClick={() =>
                            setActiveStopId(active ? null : stop.id)
                          }
                          data-bpw-swatch
                        />
                        <S.HexField active={active}>
                          <S.HexPrefix>#</S.HexPrefix>
                          <S.HexInput
                            value={stop.draft.replace(/^#/, "")}
                            aria-label={`Hex value for color ${stop.hex}`}
                            onChange={(e) =>
                              setStops((prev) =>
                                setStopDraft(prev, stop.id, e.target.value)
                              )
                            }
                            onBlur={() =>
                              setStops((prev) => snapDraft(prev, stop.id))
                            }
                            data-bpw-hex
                          />
                        </S.HexField>
                        <S.IconBtn
                          type="button"
                          aria-label="Remove color"
                          disabled={stops.length <= MIN_STOPS}
                          onClick={() =>
                            setStops((prev) => removeStop(prev, stop.id))
                          }
                          data-bpw-remove
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </S.IconBtn>
                      </S.StopRow>
                    );
                  })}
                </S.StopList>

                <S.AddRow
                  type="button"
                  disabled={stops.length >= MAX_STOPS}
                  onClick={() => setStops((prev) => addStop(prev))}
                  data-bpw-add
                >
                  <S.AddLeft>
                    <AddIcon fontSize="small" />
                    Add color
                  </S.AddLeft>
                </S.AddRow>
              </S.Field>
            </S.FormCol>
          </S.Grid>
        </S.Body>

        <Chrome.ModalFooter>
          <Chrome.ButtonsContainer>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!canSave} data-bpw-save>
              {saving ? "Saving..." : "Save palette"}
            </Button>
          </Chrome.ButtonsContainer>
        </Chrome.ModalFooter>

        {activeStop && (
          <CustomColorPicker
            visible={!!activeStop}
            color={activeStop.hex}
            onColorChange={(hex) =>
              setStops((prev) => setStopHex(prev, activeStop.id, hex))
            }
            onClose={() => setActiveStopId(null)}
            onConfirm={() => setActiveStopId(null)}
            customStyle={{
              position: "fixed",
              top: "50%",
              left: "50%",
              bottom: "auto",
              transform: "translate(-50%, -50%)",
              zIndex: 1400,
            }}
          />
        )}
      </Chrome.ModalContainer>
    </Modal>
  );
}
